/**
 * Préférences → « Clé d'agent » (brique 2 du projet MCP).
 *
 * Une seule clé par utilisateur, créée d'elle-même au premier passage : il n'y
 * a rien à configurer pour l'onglet Agent. Ce panneau sert à trois choses :
 *   - voir l'état de la clé (active ou coupée, dates, droits) ;
 *   - la RÉGÉNÉRER, pour un client externe comme Claude Code : la nouvelle clé
 *     s'affiche UNE fois, l'ancienne cesse aussitôt de fonctionner ;
 *   - COUPER l'agent : la clé est révoquée, l'agent (onglet Agent comme Claude
 *     Code) est refusé dès son appel suivant, et rien ne le relance sans vous.
 *
 * La clé en clair ne vit que dans l'état de ce composant : jamais dans un store,
 * jamais dans localStorage. Elle disparaît quand on ferme les Préférences.
 *
 * Les dernières actions viennent du journal de l'agent : chaque appel y est
 * inscrit à votre nom, refus compris.
 */
import React from 'react';
import { couperAgent, lireCleAgent, lireJournalAgent, regenererCleAgent } from '../api/agentClient';

const DROITS = {
  'regulatory:read': 'lire les fiches FDS',
  export: 'rendre et exporter',
  'projects:read': 'lire les projets',
  'projects:write': 'enregistrer les projets',
};

const NATURES = { cle: 'clé', api: 'appel', outil: 'outil', modele: 'modèle', image: 'image', validation: 'validation' };

const date = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const dateHeure = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'jamais');
const heure = (iso) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

export default function AgentKeySection() {
  const [cle, setCle] = React.useState(null);           // état renvoyé par GET /api/agent/cle
  const [clair, setClair] = React.useState(null);       // la clé en clair, juste après une régénération
  const [copiee, setCopiee] = React.useState(false);
  const [journal, setJournal] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [erreur, setErreur] = React.useState(null);

  const relireJournal = React.useCallback(() => {
    lireJournalAgent({ limite: 10 })
      .then((j) => setJournal(j.lignes || []))
      .catch(() => setJournal([]));
  }, []);

  React.useEffect(() => {
    lireCleAgent()
      .then(setCle)
      .catch((e) => setErreur(e?.status === 404
        ? "La clé d'agent n'est pas encore disponible sur ce serveur."
        : (e.message || "L'état de la clé n'a pas pu être lu.")));
    relireJournal();
  }, [relireJournal]);

  const regenerer = async () => {
    const message = cle?.etat === 'coupee'
      ? "Réactiver l'agent ? Une nouvelle clé sera créée et affichée une seule fois."
      : "Régénérer la clé ? L'ancienne cessera aussitôt de fonctionner, y compris pour un agent en cours.";
    if (!window.confirm(message)) return;
    setBusy(true); setErreur(null);
    try {
      const r = await regenererCleAgent();
      setClair(r.cle);
      setCopiee(false);
      const { cle: _jamaisGardee, avertissement: _a, ...etat } = r;
      setCle(etat);
      relireJournal();
    } catch (e) {
      setErreur(e.message || "La régénération a échoué : réessayez dans un instant.");
    } finally {
      setBusy(false);
    }
  };

  const couper = async () => {
    if (!window.confirm("Couper l'agent ? Il sera refusé dès son appel suivant, dans l'onglet Agent comme dans Claude Code. Il faudra régénérer la clé pour le relancer.")) return;
    setBusy(true); setErreur(null);
    try {
      setCle(await couperAgent());
      setClair(null);
      relireJournal();
    } catch (e) {
      setErreur(e.message || "La coupure a échoué : réessayez dans un instant.");
    } finally {
      setBusy(false);
    }
  };

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(clair);
      setCopiee(true);
    } catch {
      setErreur('La copie a échoué : sélectionnez la clé et copiez-la à la main.');
    }
  };

  const active = cle?.etat === 'active';

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {/* La sarcelle n'a pas de classe Tailwind : variable forge en style inline. */}
        <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(var(--forge-teal), 0.6)' }}></div>
        <p className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: 'rgba(var(--forge-teal), 0.8)' }}>Clé d'agent</p>
        <p className="text-[9px] text-forge-muted/60 font-mono uppercase tracking-widest ml-2">onglet agent · claude code</p>
      </div>

      <div className="bg-black/20 border border-white/5 rounded-xl p-4 shadow-inner space-y-3">
        {cle && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full"
                style={{ background: `rgba(var(--forge-${active ? 'mint' : 'rose'}), 1)` }} />
              <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-forge-text">
                {active ? 'Active' : 'Agent coupé'}
              </span>
            </div>
            <span className="text-[11px] font-mono text-forge-text tracking-wider">{cle.prefixe}…</span>
            <span className="text-[9px] font-mono uppercase tracking-widest text-forge-text/70">
              {active
                ? <>créée le {date(cle.cree_le)} · expire le {date(cle.expire_le)} · dernier usage : {dateHeure(cle.dernier_usage_le)}</>
                : <>coupée le {dateHeure(cle.revoquee_le)}</>}
            </span>
          </div>
        )}

        {active && (
          <div className="flex flex-wrap gap-1.5">
            {(cle.droits || []).map((d) => (
              <span key={d} title={d}
                className="text-[9px] font-mono uppercase tracking-widest text-forge-text/80 border border-forge-border/30 rounded-full px-2 py-0.5">
                {DROITS[d] || d}
              </span>
            ))}
          </div>
        )}

        {clair && (
          <div className="rounded-lg border border-forge-accent/40 bg-forge-accent/10 p-3 space-y-2">
            <p className="text-[10px] text-forge-text leading-relaxed">
              Voici la nouvelle clé. <strong>Copiez-la maintenant : elle ne sera plus jamais affichée.</strong> L'ancienne ne fonctionne plus.
            </p>
            <div className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-[11px] font-mono text-forge-text break-all select-all">
              {clair}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={copier} className="btn-pill-primary text-[9px]">
                {copiee ? 'Copiée' : 'Copier'}
              </button>
              <button type="button" onClick={() => setClair(null)}
                className="px-4 py-1.5 rounded-full border border-forge-border/30 bg-forge-surface/20 hover:bg-forge-surface/40 text-[9px] font-mono font-bold uppercase tracking-widest text-forge-text/80 transition-colors">
                Je l'ai rangée
              </button>
            </div>
            <p className="text-[9px] font-mono text-forge-text/70 leading-relaxed">
              Pour Claude Code : la variable d'environnement <span className="text-forge-text">LF_CLE_AGENT</span> du serveur MCP.
              Jamais dans un fichier du dépôt, jamais dans un message.
            </p>
          </div>
        )}

        {/* Pas d'action tant que la clé est affichée : un second « Régénérer »
            invaliderait celle qu'on est en train de copier. */}
        {!clair && <div className="flex items-center gap-2">
          <button type="button" onClick={regenerer} disabled={busy || !cle} className="btn-pill-primary text-[9px]">
            {busy ? '…' : active ? 'Régénérer la clé' : "Réactiver l'agent"}
          </button>
          {active && (
            <button type="button" onClick={couper} disabled={busy}
              className="px-4 py-1.5 rounded-full border border-forge-border/30 bg-forge-surface/20 hover:bg-forge-surface/40 text-[9px] font-mono font-bold uppercase tracking-widest text-forge-text/80 transition-colors disabled:opacity-50">
              Couper l'agent
            </button>
          )}
        </div>}

        {erreur && (
          <p className="text-[10px] text-forge-text leading-relaxed border border-forge-rose/40 bg-forge-rose/10 rounded-lg px-2.5 py-1.5">
            {erreur}
          </p>
        )}

        <div className="pt-1">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[9px] font-mono font-bold uppercase tracking-widest text-forge-text/70">Dernières actions de l'agent</p>
            <button type="button" onClick={relireJournal}
              className="text-[9px] font-mono uppercase tracking-widest text-forge-text/70 hover:text-forge-text transition-colors">
              Actualiser
            </button>
          </div>
          {journal.length === 0 ? (
            <p className="text-[10px] text-forge-text/70">Aucune action pour l'instant.</p>
          ) : (
            <ul className="space-y-1">
              {journal.map((l, i) => (
                <li key={`${l.horodatage}-${i}`} className="flex items-center gap-2 text-[10px] font-mono text-forge-text">
                  <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: `rgba(var(--forge-${l.ok ? 'mint' : 'rose'}), 1)` }} />
                  <span className="text-forge-text/70 shrink-0">{heure(l.horodatage)}</span>
                  <span className="uppercase tracking-widest text-[9px] text-forge-text/70 shrink-0 w-16">{NATURES[l.nature] || l.nature}</span>
                  <span className="truncate">{l.nom}</span>
                  {l.code && <span className="ml-auto text-forge-text/70 shrink-0">{l.code}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="text-[10px] text-forge-text/80 leading-relaxed pt-1">
          L'agent n'a jamais votre mot de passe : il agit avec cette clé, dans la limite de ses droits, et chacun de ses
          appels est inscrit à votre nom. La clé n'est conservée que sous forme d'empreinte : personne ne peut la relire.
        </p>
      </div>
    </div>
  );
}
